# Reflecting job ads

import { Alert, Text, Stack, TextLink } from 'braid-design-system';

<Alert tone="caution">
  <Stack space="medium">
    <Text>
      Job ad reflection is an specialised scenario which is only required where
      your software needs to retrieve job ads posted to SEEK’s employment
      marketplace to reflect them on an internal job portal.
    </Text>
    <Text>
      <TextLink href="https://au.employer.seek.com/contactus">
        Contact SEEK’s support team
      </TextLink>{' '}
      to discuss your use case before implementing this feature. This ensures
      that we can provide appropriate guidance and support for your integration.
    </Text>
  </Stack>
</Alert>

## Authentication & authorization

import { PageIcon } from '../../../components/PageIcon/PageIcon';

Follow our regular [integration process] to onboard and authenticate to the SEEK API.
You will need to supply a [partner token] when sending requests to our [GraphQL endpoint];
see our [<PageIcon icon="Auth" /> Auth][auth] documentation for more details.

SEEK will configure a [`JobPosting` relationship] with each of your hirers to authorize this scenario.
You can view a list of your SEEK hirers and their relationships on the [Developer Dashboard hirers page].

[`jobposting` relationship]: /auth/hirer-relationships
[auth]: /auth
[Developer Dashboard hirers page]: /manage/hirers
[integration process]: /introduction#integration-process
[graphql endpoint]: /graphql/in-practice
[partner token]: /auth/partner-tokens

## Job Posting process

```mermaid
sequenceDiagram
  autonumber
  participant Hirer
  participant SEEK
  participant Partner

  Hirer ->> SEEK: Post job ad
  SEEK -->> Partner: Send PositionProfilePosted event
  Partner ->> SEEK: Retrieve job ad
  Partner ->> Partner: Add job ad to search index

  Hirer ->> SEEK: Update job ad
  SEEK -->> Partner: Send PositionProfileUpdated event
  Partner ->> SEEK: Retrieve job ad
  Partner ->> Partner: Update job ad in search index
```

### 1. Post job ad

Your hirers can post job ads directly on the [SEEK Employer website] if your software has not implemented the rest of the [Job Posting] use case.
When a job ad has been successfully posted,
it will be displayed on the SEEK candidate website,
and we will queue up a [webhook] to be sent to your software.

You can test subsequent steps of this process by posting job ads with your [test hirer].

[job posting]: /use-cases/job-posting
[seek employer website]: https://employer.seek.com
[webhook]: /events/webhooks

### 2. Send PositionProfilePosted event

Expose a public HTTPS endpoint to receive [webhooks] from the SEEK API.

Then, [configure a webhook subscription] through the [Developer Dashboard webhooks page].
A [signed], partner-scoped subscription is recommended in production for enhanced security and ease of maintenance.

| Field                    | Value                   |
| :----------------------- | :---------------------- |
| [`eventTypeCode`]        | `PositionProfilePosted` |
| [`schemeId`]             | `seekAnz`               |
| [`signingAlgorithmCode`] | `SeekHmacSha512`        |

Once the subscription is in place,
we will POST to your HTTPS endpoint when a job ad is successfully posted to SEEK.

This is an example of a [webhook] body containing a [`PositionProfilePosted` event]:

```jsonc sample-webhook-body
{
  "events": [
    {
      "id": "seekAnzPublicTest:event:events:KNYEK91zoe8JoZU9iv6853",
      "type": "PositionProfilePosted",
      "createDateTime": "2019-08-20T21:02:24.611Z",
      // This can be passed to the `positionProfile` query
      "positionProfileId": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV",
      // This is only available for signed webhook subscriptions
      "hirerId": "seekAnzPublicTest:organization:seek:93WyyF1h"
    }
  ],
  "subscriptionId": "seekAnzPublicTest:webhookSubscription:events:Kyak8JfPKhHCAXM2s9b9wX",
  "url": "https://example.com/webhook",
  "expireDateTime": "2019-09-13T22:16:10.593Z"
}
```

[`eventTypeCode`]: /schema/named-type/CreateWebhookSubscription_SubscriptionInput#/field/eventTypeCode
[`PositionProfilePosted` event]: /schema/webhooks/PositionProfilePostedEvent
[`schemeId`]: /schema/named-type/CreateWebhookSubscription_SubscriptionInput#/field/schemeId
[`signingAlgorithmCode`]: /schema/named-type/CreateWebhookSubscription_SubscriptionInput#/field/signingAlgorithmCode
[configure a webhook subscription]: /events/webhooks#subscription-management
[Developer Dashboard webhooks page]: /manage/webhooks
[signed]: /events/webhooks#security
[webhooks]: /events/webhooks

### 3. Retrieve job ad

You can retrieve job ad details from the SEEK API by supplying the `positionProfileId` from the prior step to the [`positionProfile` query];
see [querying a job ad] for more details.

Review the [`PostedPositionProfile`] object and use the job ads posted with your test hirer to decide which fields you wish to retrieve.
This is an example query which additionally selects [branding images] associated with the job ad:

```graphql
query ($id: String!) {
  positionProfile(id: $id) {
    postingInstructions {
      branding {
        images {
          typeCode
          url
        }
      }
    }
  }
}
```

```json
{
  "id": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV"
}
```

```json
{
  "positionProfile": {
    "postingInstructions": [
      {
        "branding": {
          "images": [
            {
              "typeCode": "OriginalLogo",
              "url": "https://image-service-cdn.seek.com.au/27ecca452d06d960b596aa40380f0d06c56b6ba8"
            }
          ]
        }
      }
    ]
  }
}
```

Only include fields your software actually needs in the GraphQL query.
This allows us to evolve our GraphQL schema without requiring adjustments to your software for field changes and deprecations that do not materially affect your end-user experience.

[`positionprofile` query]: /schema/query/positionProfile
[`postedpositionprofile`]: /schema/named-type/PostedPositionProfile
[branding images]: /use-cases/job-posting/branding
[querying a job ad]: /use-cases/job-posting/managing-job-ads/querying-a-job-ad
[test hirer]: /introduction#integration-process

### 4. Add job ad to search index

The SEEK API does not offer programmatic job search capabilities.
Your software will need to ingest job ads into a search index to serve your internal job portal.

### 5. Update job ad

Your hirers may edit their live job ads on the [SEEK Employer website].
Updates to a job ad will be reflected on the SEEK candidate website,
and we will queue up a [webhook] to be sent to your software.

### 6. Send PositionProfileUpdated event

As with Step 2,
[configure a webhook subscription] through the [Developer Dashboard webhooks page].

| Field                    | Value                    |
| :----------------------- | :----------------------- |
| [`eventTypeCode`]        | `PositionProfileUpdated` |
| [`schemeId`]             | `seekAnz`                |
| [`signingAlgorithmCode`] | `SeekHmacSha512`         |

Once the subscription is in place,
we will POST a [`PositionProfileUpdated` event] to your HTTPS endpoint whenever a live job ad is updated on SEEK.

[`PositionProfileUpdated` event]: /schema/webhooks/PositionProfileUpdatedEvent

## Job closure process

Our [job ad lifecycle] specifies a maximum period of 30 days for a job ad.
Once a job ad reaches its scheduled end date,
SEEK will automatically close it and queue up a [webhook] to be sent to your software.

```mermaid
sequenceDiagram
  autonumber
  participant SEEK
  participant Partner

  SEEK ->> SEEK: Close job ad on schedule
  SEEK -->> Partner: Send PositionProfileClosed event
  Partner ->> Partner: Remove job ad from search index
```

While you can retrieve the scheduled end date as part of the job ad,
we recommend subscribing to the `PositionProfileClosed` event.
You can [configure a webhook subscription] through the [Developer Dashboard webhooks page].
This will cover the scenario of a hirer or SEEK support member manually closing the job ad ahead of schedule.

```mermaid
sequenceDiagram
  autonumber
  participant Hirer
  participant SEEK
  participant Partner

  Hirer ->> SEEK: Close job ad ahead of schedule
  SEEK -->> Partner: Send PositionProfileClosed event
  Partner ->> Partner: Remove job ad from search index
```

An example of a [webhook] body containing a `PositionProfileClosed` event can be found in [closing a job ad].
Use this event to remove the job ad from the search index that serves your internal job portal.

[closing a job ad]: /use-cases/job-posting/managing-job-ads/closing-a-job-ad#positionprofileclosed
[job ad lifecycle]: /use-cases/job-posting/key-concepts#job-ad-lifecycle

## Application process

[SEEK’s Apply Form] allows the application process to be hosted on SEEK.

- The reflected listing on your internal job board should link back to the SEEK job details page where the candidate can apply for the job.

  You can retrieve this URL from the [`PostedPositionProfile.positionUri` field].

- If the candidate can view the full details of the job ad on your internal job board listing,
  you can skip SEEK’s job details page and link directly to SEEK’s Apply Form.

  You can retrieve this URL from the [`PostedPositionProfile.seekApplicationUri` field].

```graphql
query ($id: String!) {
  positionProfile(id: $id) {
    positionUri

    ... on PostedPositionProfile {
      seekApplicationUri {
        url
      }
    }
  }
}
```

```json
{
  "id": "seekAnzPublicTest:positionProfile:jobAd:2782PZfXV"
}
```

```json
{
  "positionProfile": {
    "positionUri": "https://au.candidate.example.seek.com/job/30000000",
    "seekApplicationUri": {
      "url": "https://au.candidate.example.seek.com/job/30000000/apply"
    }
  }
}
```

Your hirers will be able to manage candidates for a job ad on the [SEEK Employer website].
Implement the [Optimised Apply] use case if you need to ingest applications into your applicant tracking system.

[`PostedPositionProfile.positionUri` field]: /schema/named-type/PostedPositionProfile#/field/positionUri
[`PostedPositionProfile.seekApplicationUri` field]: /schema/named-type/PostedPositionProfile#/field/seekApplicationUri
[Optimised Apply]: /use-cases/application-export
[SEEK’s Apply Form]: /use-cases/job-posting/application-methods#seeks-apply-form
